import React, { useEffect, useState } from 'react';
import { partLine } from './diagnosisEstimate';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Package,
  Shield,
  ShoppingCart,
  Truck
} from 'lucide-react';

// ENHANCED PARTS SECTION COMPONENT
//
// Hoisted verbatim out of JobManagement's function body (Task 6). It was
// redefined on every render there, remounting its own selectedParts/quantities
// state each time.
//
// Closed-over parent-scope values: none.
//
// The ticked parts ARE this part of the estimate: every change is reported up
// as invoice lines through onSelectionChange, and the diagnosis modal's single
// Create Estimate button turns them (plus any suggestions) into one. It used to
// take jobData/onAddToEstimate/onCreateEstimate and had its own Create
// Estimate button, which sent a different set of parts from the modal's.
  const EnhancedPartsSection = ({ 
    suggestedParts, 
    onSelectionChange
  }) => {
    const [selectedParts, setSelectedParts] = useState(new Set());
    const [quantities, setQuantities] = useState({});

    // Claude's parts have no part number (it is told never to invent one),
    // so selection can't be keyed on it alone: every '' was the same part.
    const keyOf = (part, index) => part.part_number || `${index}:${part.part_name}`;

    useEffect(() => {
      onSelectionChange?.(
        (suggestedParts || [])
          .filter((part, index) => selectedParts.has(keyOf(part, index)))
          .map((part, index) => partLine(part, quantities[keyOf(part, index)] || part.quantity || 1))
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedParts, quantities, suggestedParts]);
    
    const totalPartsCount = suggestedParts?.length || 0;
    const totalPartsCost = suggestedParts?.reduce(
      (sum, part, index) => sum + (part.estimated_cost * (quantities[keyOf(part, index)] || part.quantity || 1)), 
      0
    ) || 0;

    const togglePartSelection = (partNumber) => {
      const newSelected = new Set(selectedParts);
      if (newSelected.has(partNumber)) {
        newSelected.delete(partNumber);
      } else {
        newSelected.add(partNumber);
      }
      setSelectedParts(newSelected);
    };

    const updateQuantity = (partNumber, quantity) => {
      setQuantities(prev => ({
        ...prev,
        [partNumber]: Math.max(1, parseInt(quantity) || 1)
      }));
    };

    const getSelectedPartsTotal = () => {
      return suggestedParts
        ?.reduce((sum, part, index) => (
          selectedParts.has(keyOf(part, index))
            ? sum + (part.estimated_cost * (quantities[keyOf(part, index)] || part.quantity || 1))
            : sum
        ), 0) || 0;
    };

    const getPartSourceBadge = (part) => {
      if (part.source === 'parts_database') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-small font-bold bg-accent-hover text-white">
            <span className="w-2 h-2 bg-surface-card rounded-full mr-2 animate-pulse"></span>
            Live Data
          </span>
        );
      } else if (part.source === 'ai_estimate') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-small font-medium bg-surface-sunken text-ink border">
            <span className="w-2 h-2 bg-ink-muted rounded-full mr-2"></span>
            AI estimate
          </span>
        );
      } else if (part.source === 'fallback') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-small font-medium bg-surface-sunken text-ink border">
            <span className="w-2 h-2 bg-ink-muted rounded-full mr-2"></span>
            Estimated
          </span>
        );
      }
      return null;
    };

    const getAvailabilityStatus = (availability) => {
      const status = availability?.toLowerCase() || '';
      if (status.includes('in stock') || status.includes('available')) {
        return (
          <span className="inline-flex items-center px-2 py-1 text-small font-medium bg-success-tint text-success rounded-full">
            <CheckCircle className="w-3 h-3 mr-1" />
            In Stock
          </span>
        );
      } else if (status.includes('limited') || status.includes('low')) {
        return (
          <span className="inline-flex items-center px-2 py-1 text-small font-medium bg-warning-tint text-warning rounded-full">
            <AlertCircle className="w-3 h-3 mr-1" />
            Limited
          </span>
        );
      } else if (status.includes('out') || status.includes('unavailable')) {
        return (
          <span className="inline-flex items-center px-2 py-1 text-small font-medium bg-danger-tint text-danger rounded-full">
            <Clock className="w-3 h-3 mr-1" />
            Special Order
        </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2 py-1 text-small font-medium bg-accent/20 text-accent rounded-full">
          <Truck className="w-3 h-3 mr-1" />
          Check Stock
        </span>
      );
    };

    // A diagnosis with nothing to replace (the next step is more diagnosis)
    // has no parts; an empty block only told the mechanic to re-run it.
    if (!suggestedParts || suggestedParts.length === 0) {
      return null;
    }

    return (
      <div className="space-y-6">
        {/* Parts Overview Header */}
        <div className="bg-accent-tint rounded-xl p-6 border border-accent/30">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-ink flex items-center">
              <div className="p-2 bg-accent/20 rounded-lg mr-3">
                <Package className="h-5 w-5 text-accent" />
              </div>
              Recommended Parts ({totalPartsCount})
            </h4>
            <div className="text-right">
              <div className="text-sm text-ink-muted">Total Value</div>
              <div className="text-page-title text-accent">
                ${totalPartsCost.toFixed(2)}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-lg font-bold text-ink">{totalPartsCount}</div>
              <div className="text-ink-muted">Parts Found</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-success">{selectedParts.size}</div>
              <div className="text-ink-muted">Selected</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-accent">${getSelectedPartsTotal().toFixed(2)}</div>
              <div className="text-ink-muted">Selected Total</div>
            </div>
          </div>
        </div>

        {/* Parts List */}
        <div className="space-y-4">
          {suggestedParts.map((part, index) => {
            const key = keyOf(part, index);
            const quantity = quantities[key] || part.quantity || 1;
            const lineTotal = part.estimated_cost * quantity;
            const isSelected = selectedParts.has(key);
            
            return (
              <div 
                key={index} 
                className={`bg-surface-card rounded-card border transition-colors duration-state ${
                  isSelected
                    ? 'border-accent bg-accent-tint'
                    : 'border-border-default hover:border-border-strong'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    {/* Part Selection & Info */}
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Selection Checkbox */}
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => togglePartSelection(key)}
                          className="w-5 h-5 text-accent bg-surface-sunken border-border-default rounded focus:ring-accent focus:ring-2"
                        />
                      </div>
                      
                      {/* Part Details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h5 className="font-semibold text-ink text-lg">{part.part_name}</h5>
                          {getPartSourceBadge(part)}
                          {part.is_oem === true && (
                            <span className="inline-flex items-center px-2 py-1 bg-success-tint text-success text-small font-bold rounded-full">
                              <Shield className="w-3 h-3 mr-1" />
                              OEM
                            </span>
                          )}
                          {part.is_oem === false && (
                            <span className="inline-flex items-center px-2 py-1 bg-warning-tint text-warning text-small font-medium rounded-full">
                              Aftermarket
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                          {part.part_number && (
                            <div>
                              <span className="text-ink-muted font-medium">Part #:</span>
                              <div className="font-mono text-ink">{part.part_number}</div>
                            </div>
                          )}
                          {part.brand && (
                            <div>
                              <span className="text-ink-muted font-medium">Brand:</span>
                              <div className="font-medium text-ink">{part.brand}</div>
                            </div>
                          )}
                          <div>
                            <span className="text-ink-muted font-medium">Category:</span>
                            <div className="font-medium text-ink">{part.category}</div>
                          </div>
                          <div>
                            <span className="text-ink-muted font-medium">Availability:</span>
                            <div className="mt-1">{getAvailabilityStatus(part.availability)}</div>
                          </div>
                        </div>
                        
                        {part.fitment_notes && (
                          <div className="mt-3 p-3 bg-accent/10 rounded-lg border border-accent/30">
                            <div className="text-sm">
                              <span className="font-medium text-accent">Fitment Notes:</span>
                              <span className="text-accent ml-2">{part.fitment_notes}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Quantity & Pricing */}
                    <div className="text-right ml-6 space-y-4">
                      <div>
                        <div className="text-page-title text-ink">
                          ${lineTotal.toFixed(2)}
                        </div>
                        <div className="text-sm text-ink-muted">
                          ${part.estimated_cost.toFixed(2)} each
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-ink">Qty:</label>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={quantity}
                          onChange={(e) => updateQuantity(key, e.target.value)}
                          className="w-16 px-2 py-1 text-sm border border-border-strong rounded-md focus:ring-2 focus:ring-accent focus:border-accent"
                        />
                      </div>
                      
                      <button
                        onClick={() => togglePartSelection(key)}
                        className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-accent text-white hover:bg-accent-hover'
                            : 'bg-surface-sunken text-ink hover:bg-surface-sunken'
                        }`}
                      >
                        <ShoppingCart className="w-4 h-4 inline mr-2" />
                        {isSelected ? 'Added' : 'Add to Estimate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        {selectedParts.size > 0 && (
          <div className="bg-surface-card rounded-xl border border-border-default p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold text-ink">
                  {selectedParts.size} parts selected
                </div>
                <div className="text-sm text-ink-muted">
                  Total: ${getSelectedPartsTotal().toFixed(2)}
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedParts(new Set())}
                  className="px-4 py-2 border border-border-default rounded-lg text-ink hover:bg-surface-sunken transition-colors"
                >
                  Clear Selection
                </button>
              </div>
                      </div>
        </div>
      )}


    </div>
  );
};

export default EnhancedPartsSection;
